<script setup>
    import SessionsSpeakers from '~/components/blocks/two-six/sessions/speakers.vue';
    import SessionsRelated from '~/components/blocks/two-six/sessions/related.vue';
    import SessionsTags from '~/components/blocks/two-five/sessions/tags.vue';
    import Cta from '~/components/blocks/two-six/cta.vue';
    import { useHeroAltLogo } from '@/composables/useHeroAltLogo';
    import { getConferenceTimezone, getConferenceTimezoneLabel, isArchivedSession, isSessionOnDemand } from '~~/shared/utilities/schedule';

    const dayjs = useDayjs();
    // Dark "alt" header logo. This hero renders outside the Builder block list
    // (a standalone Nuxt page), so the composable treats it as the page hero.
    const rootRef = ref(null);
    useHeroAltLogo(rootRef);

    const { $bus, $clientPosthog } = useNuxtApp();
    const { loggedIn, user, fetch: refreshSession } = useUserSession();
    const userApi = useUserApi();

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
    });

    const isFavorite = ref(null)
    const busy = ref(false)


    const handleSkillClick = async (level) => {
        await navigateTo({ path: '/2025-sessions', query: { level: level } });
    };

    const duration = computed(() => {
        if (props.session.start_date_and_time && props.session.end_date_and_time) {
            const start = new Date(props.session.start_date_and_time);
            const end = new Date(props.session.end_date_and_time);
            const diffMs = end - start;
            if (diffMs > 0) {
                const diffMins = Math.round(diffMs / 60000);
                const hours = Math.floor(diffMins / 60);
                const mins = diffMins % 60;
                const pluralMins = mins > 1 ? 'minutes' : 'minute';
                const pluralHours = hours > 1 ? 'hours' : 'hour';
                if (hours && mins) return `${hours} ${pluralHours} ${mins} ${pluralMins}`;
                if (hours) return `${hours} ${pluralHours}`;
                return `${mins} ${pluralMins}`;
            }
        }
        return null;
    });

    const location = computed(() => {
        if (typeof props.session.locations === 'string') return props.session.locations;
        if (Array.isArray(props.session.locations)) return props.session.locations[0];
        return '';
    });

    const level = computed(() => {
        if (typeof props.session.level === 'string') return props.session.level;
        if (Array.isArray(props.session.level)) return props.session.level[0];
        return '';
    });

    const handleFavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } })
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = true;
            await userApi.favorite(props.session.unique_id);
            await refreshSession();
            $bus.emit('favorited', props.session);
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            console.error('Error favoriting session:', error);
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const is_favorited = computed(() => {
        // Check if user.favorites exists and contains the session unique_id
        const userHasFavorited = user.value?.favorites?.includes(props.session.unique_id);
        return isFavorite.value === null ? userHasFavorited : isFavorite.value;
    });

    const handleUnfavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } })
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = false;
            await userApi.unfavorite(props.session.unique_id);
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            console.error('Error favoriting session:', error);
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const button = {
        url: '/register',
        label: 'Register Now',
    };

    const buttonStyles = {
        base: 'bg-transparent border-[var(--magenta)] text-[var(--magenta)] font-hubspot-sans font-normal text-[17px] tracking-[0.34px] rounded-[50px] hover:bg-[var(--magenta)] hover:text-white hover:border-[var(--magenta)]',
        leadingIcon: 'mr-2',
    };

    const buttonStylesOrange = {
        base: 'bg-[var(--orange)] border-1 border-[var(--orange)] text-white font-hubspot-serif font-normal text-[17px] tracking-[0.34px] rounded-[50px] hover:bg-[var(--magenta)] hover:border-[var(--magenta)] hover:text-white',
        leadingIcon: 'mr-2',
    };

    const buttonStylesIcon = {
        base: 'bg-transparent content-center w-[2.6875rem] h-[2.6875rem] border-[var(--magenta)] text-[var(--magenta)] font-medium text-[0.9375rem] tracking-[-0.01875rem] hover:text-[var(--orange)] hover:bg-transparent',
        leadingIcon: 'mr-0 flex items-center justify-center w-full',
    };

    const handleAddToGoogle = () => {
        const icalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(props.session.title)}&dates=${props.session.start_date_and_time.replace(/[-:]/g, '')}/${props.session.end_date_and_time.replace(/[-:]/g, '')}&details=${encodeURIComponent(props.session.description)}&location=${encodeURIComponent(location.value.name)}`;
        window.open(icalUrl, '_blank');
    };

    // The page's Save a Seat control lives in FragmentsDrawersSessionSidebar,
    // which this header mounts below — these handlers lost their buttons in the
    // 2026 reskin and have had no caller since.

    const handleAddToOutlook = () => {
        const start = new Date(props.session.start_date_and_time).toISOString().replace(/[-:]/g, '');
        const end = new Date(props.session.end_date_and_time).toISOString().replace(/[-:]/g, '');
        const outlookUrl = `https://outlook.live.com/owa/?path=/calendar/action/compose&subject=${encodeURIComponent(props.session.title)}&startdt=${start}&enddt=${end}&body=${encodeURIComponent(props.session.description)}&location=${encodeURIComponent(location.value.name)}`;
        window.open(outlookUrl, '_blank');
    };

    const handleAddToAppleIcal = () => {
        const pad = (num) => String(num).padStart(2, '0');
        const formatDate = (dateStr) => {
            const d = new Date(dateStr);
            return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
        };
        const start = formatDate(props.session.start_date_and_time);
        const end = formatDate(props.session.end_date_and_time);
        const locationStr = typeof location.value === 'object' ? location.value.name : location.value;
        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            `SUMMARY:${props.session.title}`,
            `DESCRIPTION:${props.session.description}`,
            `DTSTART:${start}`,
            `DTEND:${end}`,
            `LOCATION:${locationStr}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\r\n');
        const blob = new Blob([icsContent], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${props.session.title.replace(/\s+/g, '_')}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const isArchive = computed(() => isArchivedSession(props.session));

    // Is there a pill to the LEFT of the on-demand glyph? The glyph's 10px
    // offset — which makes the design's 20px on top of the row's gap-[10px] —
    // is only correct when something precedes it. With no pills the row starts
    // at the glyph, and an unconditional margin indented it away from the left
    // edge that the date/time line below it sits on.
    const hasLeadingPill = computed(() => !!(
        isArchive.value
        || props.session?.session_reservations
        || props.session?.session_type?.[0]?.name
    ));

    // Date • time • location line, matching the session cards / detail tray.
    const formattedDateTime = computed(() => {
        if (!props.session.start_date_and_time) return '';
        const tz = getConferenceTimezone(2026);
        const tzLabel = getConferenceTimezoneLabel(2026);
        const start = dayjs(props.session.start_date_and_time).tz(tz);
        const end = props.session.end_date_and_time ? dayjs(props.session.end_date_and_time).tz(tz) : null;
        const dayStr = start.format('ddd');
        const dateStr = start.format('MMM D');
        const startStr = start.format('h:mma');
        const endStr = end ? end.format('h:mma') : '';
        const timeRange = end ? `${startStr} – ${endStr} ${tzLabel}` : `${startStr} ${tzLabel}`;
        // nbsp + space each side of the bullet so two spaces render (HTML
        // collapses plain double-spaces to one) — matches the session cards.
        const sep = '  •  ';
        return `${dayStr}${sep}${dateStr}${sep}${timeRange}`;
    });

    const sessionLocationName = computed(() =>
        props.session.locations?.[0]?.name || props.session.location?.[0]?.name || ''
    );

    // 2026 sessions (Sessionboard) expose session_type, session_theme, session_role
    // directly on the session object — the legacy two-five SessionsTags component
    // uses 2025 Airtable field handles which don't exist on 2026 data.
    const tags2026 = computed(() => {
        const s = props.session;
        // Session Type is already shown as the eyebrow above the date/time, so
        // it's intentionally omitted from the tag row to avoid duplication.
        const themes = (s.session_theme || []).map(t => ({ ...t, param: `themes=${t.id}` }));
        const roles  = (s.session_role  || []).map(t => ({ ...t, param: `role=${t.id}` }));
        return [...themes, ...roles];
    });

    const shareLink = computed(() => {
        const slug = (props.session.unique_id || props.session.id || '').toLowerCase();
        if (!slug) return undefined;
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/sessions/${slug}`;
    });

    const transformedAttachments = computed(() => {
        const { attachments, attachments_name } = props.session;

        if (!attachments || attachments.length === 0) {
            return [];
        }

        const names = attachments_name ? attachments_name.split(',').map(name => name.trim()) : [];

        return attachments.map((attachment, index) => ({
            url: attachment.url,
            name: names[index] || attachment.filename || `Attachment ${index + 1}`
        }));
    });

</script>

<template>
    <div ref="rootRef" class="agenda-page" v-if="session">
        <section class="block-agenda-header relative overflow-hidden bg-[var(--pink)]
            px-[var(--box-padding-lr-m)] pt-[90px] pb-[30px]
            md:px-[var(--box-padding-lr)] md:pt-[140px] md:pb-[50px]
            font-hubspot-sans"
            :class="{ 'has-reservation': session.reservations_required, 'has-speakers': session.speakers && session.speakers.length > 0 }">

            <!-- Decorative shard (desktop only). Pulled up at the lg
                 breakpoint so long titles/descriptions don't collide with
                 the shard; restored at xl where there's room. The transition
                 makes the snap between breakpoints animate during a window
                 resize instead of teleporting. -->
            <img src="/img/2026/sessions/hero-shard.png"
                alt="" aria-hidden="true"
                class="hidden lg:display-block absolute -top-[150px] xl:top-0 right-0 w-[559px] h-auto pointer-events-none z-0
                    transition-[top] duration-300 ease-out" />

            <!-- Back-to-agenda breadcrumb -->
            <MessLink href="/agenda"
                class="relative z-[1] inline-flex items-center gap-[11px] no-underline
                    font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase
                    text-[#7d4566] hover:text-[var(--magenta)] transition-colors
                    mb-[32px]">
                <svg width="7" height="7" viewBox="0 0 8.5 8.5" fill="none" aria-hidden="true">
                    <path d="M7.75 4.25H0.75M0.75 4.25L4.05556 0.750002M0.75 4.25L4.05556 7.75"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                All Sessions
            </MessLink>

            <!-- Hero content + action sidebar (two-column on lg+). The fixed 440px
                 card sits at the container's right edge and the left text column
                 takes everything else, so the gutter stays AT 100px instead of
                 absorbing the surplus.
                 The left column used to cap at 804px, which meant every pixel past
                 that went into the gap: 301px at 1625, 1236px at 2560, leaving the
                 action card stranded from the copy it belongs to (UNB-616). -->
            <!-- gap-[20px]: stacked on mobile, the share row and the schedule
                 sidebar butted together with no space (UNB-617). The lg:gap-[100px]
                 only applies once this becomes a row, so the base gap covers the
                 stacked case. -->
            <div class="relative z-[1] flex flex-col gap-[20px] lg:flex-row lg:items-start lg:justify-between lg:gap-[100px] w-full">
                <div class="flex flex-col gap-[40px] flex-1 min-w-0">
                    <div class="flex flex-col gap-[26px]">
                        <!-- Archive pill (2025 sessions) OR limited capacity + session type pills (2026) -->
                        <div class="flex flex-wrap gap-[10px] items-center w-fit">
                            <span v-if="isArchive"
                                class="inline-flex w-fit items-center rounded-[25px] border border-(--dark-pink) px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-(--dark-pink)">
                                Archive
                            </span>
                            <template v-else>
                                <span v-if="session.session_reservations"
                                    class="inline-flex items-center rounded-[25px] border border-(--magenta) bg-(--magenta) px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-white">
                                    Limited Capacity
                                </span>
                                <span v-if="session.session_type?.[0]?.name"
                                    class="inline-flex items-center rounded-[25px] border border-[#7D4566] px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-[#7D4566]">
                                    {{ session.session_type[0].name }}
                                </span>
                            </template>
                        <!-- On-demand glyph (Figma 4583:186710 "Union",
                             15.128 x 9.619). Sits immediately right of
                             the pills, in --darker-pink, matching the
                             session-type pill it follows.

                             ml-[10px] on top of the row's gap-[10px]
                             makes the 20px the design specifies between
                             pill and glyph, without widening the gap
                             BETWEEN the pills, which the design keeps
                             tighter.

                             Sized 15x10 rather than the raw 15.128 x
                             9.619: that is what the three agenda cards
                             already use for this same glyph, and a
                             fourth sub-pixel variant would only make the
                             set inconsistent.

                             Outside the v-else, so an archived session
                             gets it too — every session carrying the
                             flag today is a 2025 archive. -->
                        <UIcon
                            v-if="isSessionOnDemand(session)"
                            name="local-on-demand"
                            aria-hidden="true"
                            class="shrink-0 w-[15px] h-[10px] text-[#7D4566]"
                            :class="hasLeadingPill ? 'ml-[10px]' : ''"
                        />
                        </div>

                        <!-- Date • time • location -->
                        <p v-if="!isArchive && formattedDateTime"
                            class="font-hubspot-sans font-normal text-[14px] leading-[1.5] tracking-[0.21px] text-(--magenta) m-0">
                            {{ formattedDateTime }}<template v-if="sessionLocationName">&nbsp; •&nbsp; {{ sessionLocationName }}</template>
                        </p>

                        <h1 v-html="session.title"
                            class="font-hubspot-serif font-normal text-[36px] lg:text-[60px] leading-[1.1] text-[var(--magenta)] m-0"></h1>

                        <div v-if="session.description" v-html="session.description"
                            class="session-description font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] text-[var(--magenta)]"></div>

                        <!-- YouTube embed -->
                        <FragmentsVideoSessionEmbed :embed-id="session.youtube_embed_id" />

                        <div v-if="tags2026.length" class="session-tags-list tags flex flex-row flex-wrap gap-[8px]">
                            <NuxtLink v-for="tag in tags2026" :key="tag.id" :to="`/agenda?${tag.param}`">
                                <ProjectTag colorSet="session-tag" :text="tag.name" :useHover="true" :useX="false" gtmEventName="session_tag_clicked" />
                            </NuxtLink>
                        </div>
                        <SessionsTags v-else :session="session" :tags="[]" />
                    </div>

                    <!-- AI recommendation callout. The session tray has carried this
                         since UNB-599 and the detail page never did, so the same
                         session read as builder-recommended in the tray and plain on
                         its own page (UNB, Shannon: "seeing on tray but NOT on
                         page"). Same markup and same --darker-pink as the tray: this
                         header sits on --pink, so the #FCD4E5 treatment belongs to
                         the dark CARDS, not here. -->
                    <div
                        v-if="session.recommendation"
                        data-test="page-reason-callout"
                        class="border border-(--darker-pink) rounded-[10px] px-[19px] py-[17px] flex items-start gap-[10px] w-full">
                        <svg class="shrink-0 mt-[2px] size-[15px] text-(--darker-pink)" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                            <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" />
                            <path d="M11.5263 8.46995C11.5263 8.54151 11.4683 8.59954 11.3973 8.59954C10.264 8.59954 8.92285 9.94044 8.92285 11.0734C8.92285 11.145 8.86481 11.203 8.7938 11.203C8.7228 11.203 8.66476 11.145 8.66476 11.0734C8.66419 9.94044 7.32302 8.59954 6.18978 8.59954C6.11821 8.59954 6.06073 8.54151 6.06073 8.46995C6.06073 8.3984 6.11877 8.34037 6.18978 8.34037C7.32302 8.34037 8.66419 6.99947 8.66419 5.86703C8.66419 5.79547 8.72224 5.73744 8.79324 5.73744C8.86424 5.73744 8.92229 5.79547 8.92229 5.86703C8.92229 6.99947 10.2635 8.34037 11.3967 8.34037C11.4683 8.34037 11.5258 8.3984 11.5258 8.46995H11.5263Z" fill="white" />
                            <path d="M7.2753 5.73726C7.2753 5.78496 7.23661 5.82364 7.18927 5.82364C6.43378 5.82364 5.53966 6.71758 5.53966 7.47292C5.53966 7.52062 5.50096 7.55931 5.45363 7.55931C5.40629 7.55931 5.3676 7.52062 5.3676 7.47292C5.36722 6.71758 4.4731 5.82364 3.71761 5.82364C3.6699 5.82364 3.63158 5.78496 3.63158 5.73726C3.63158 5.68955 3.67027 5.65087 3.71761 5.65087C4.4731 5.65087 5.36722 4.75693 5.36722 4.00197C5.36722 3.95427 5.40592 3.91558 5.45325 3.91558C5.50059 3.91558 5.53928 3.95427 5.53928 4.00197C5.53928 4.75693 6.4334 5.65087 7.1889 5.65087C7.23661 5.65087 7.27493 5.68955 7.27493 5.73726H7.2753Z" fill="white" />
                        </svg>
                        <p class="flex-1 font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--darker-pink) m-0">{{ session.recommendation }}</p>
                    </div>

                    <!-- Share row -->
                    <div class="session-hero-share flex flex-col lg:flex-row lg:items-center gap-[20px] lg:gap-[39px]">
                        <FragmentsSponsorsSponsoredBy :sponsors="session.sponsors" />
                        <slot name="share">
                            <FragmentsShare :title="session.title" :link="shareLink" label="Share:" copyToClipboard />
                        </slot>
                    </div>
                </div>

                <!-- Client-only: the sidebar's variant is driven entirely by per-user
                     state (user.scheduled / user.sessionRegistration), and this page's
                     HTML is CDN-cached — a server-rendered rail would be frozen/wrong and
                     its root class binding gets stuck after hydration. Render on the client. -->
                <ClientOnly>
                    <FragmentsDrawersSessionSidebar :session="session" class="w-full lg:w-[440px] lg:shrink-0" />
                </ClientOnly>
            </div>
        </section>
        <SessionsSpeakers :session="session"></SessionsSpeakers>
        <SessionsRelated :session="session"></SessionsRelated>
        <Cta
            variant="orange"
            headline="Be There at UNBOUND 2026"
            copy="Boston. September 16&ndash;18. Three days designed to help you move beyond outdated playbooks. Break beyond the limits that defined yesterday. Be unstoppable."
            :button="button" />
    </div>
</template>

<style>
    /* Negative margin pulls the hero under the sticky nav so the pink bg
       bleeds to the top of the viewport. Padding is handled by Tailwind
       utilities on the element. */
    .block-agenda-header {
        margin-top: calc(var(--header-height) * -1);
        color: var(--magenta);
    }

    @media (min-width: 1024px) {
        .block-agenda-header {
            margin-bottom: 0;
        }
    }

    /* [child] targets block-session-speakers child component */
    @media (min-width: 1024px) {
        .block-agenda-header .block-session-speakers {
            margin-top: 0;
        }
    }

    /* [child] targets badge/label elements rendered inside the header */
    .block-agenda-header .required-reservation {
        width: fit-content;
        display: flex;
        color: white;
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1.1875rem;
        letter-spacing: 0.0375rem;
        text-transform: uppercase;
        padding: 0.375rem 1rem;
        background-color: var(--orange);
        border-radius: var(--full-radius);
        margin-bottom: 1.5625rem;
    }

    /* [state] compound .required-reservation.full selector */
    .block-agenda-header .required-reservation.full {
        background-color: var(--magenta);
    }

    .block-agenda-header .on-demand {
        width: fit-content;
        display: flex;
        align-items: center;
        color: var(--magenta);
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.1875rem;
        padding: 0.375rem 1rem;
        background-color: transparent;
        border-radius: var(--full-radius);
        border: 1px solid var(--magenta);
        margin-bottom: 0.9375rem;
        gap: 0.5rem;
    }

    /* [state] compound span.bullet:last-child selector to hide trailing bullet */
    .block-agenda-header .header .time span.bullet:last-child {
        display: none;
    }

    /* [child] targets session-tags-list child component elements */
    .block-agenda-header .header .session-tags-list {
        margin-bottom: 3rem;
    }

    .block-agenda-header .header .session-tags-list .fs-12 {
        letter-spacing: 0.0375rem;
    }

    /* [child] targets skill-level element with interactive hover state */
    .block-agenda-header .header .skill-level {
        width: fit-content;
        font-size: 0.6875rem;
        font-weight: 400;
        line-height: 0.6875rem;
        letter-spacing: 0.034375rem;
        text-transform: uppercase;
        margin-bottom: 1.875rem;
        padding: 0.4375rem 0.75rem;
        border-radius: 50px;
        border: 1px solid var(--magenta);
        cursor: pointer;
    }

    @media (hover: hover) {
        .block-agenda-header .header .skill-level:hover {
            background-color: var(--magenta);
            color: white;
        }
    }

    /* [child] targets sponsored-by element inside child component */
    .block-agenda-header .session-hero-share .sponsored-by {
        margin-bottom: 0;
    }

    /* Session description block spacing — give rich-text children 1rem bottom margin */
    .block-agenda-header .session-description :is(p, ul, ol, dl, blockquote):not(:last-child) {
        margin-bottom: 1rem;
    }

    .block-agenda-header .session-description :is(ul, ol) {
        padding-left: 1.25rem;
        list-style: revert;
    }

    .block-agenda-header .session-description li {
        margin-bottom: 0.25rem;
    }

    @media (min-width: 1024px) {
        .block-agenda-header .session-hero-share .sponsored-by {
            margin-top: 0;
            margin-right: 3.25rem;
        }
    }
</style>
